import {
  forwardRef,
  InputHTMLAttributes,
  memo,
  MutableRefObject,
  ReactNode,
  useId,
  useRef,
} from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';

type InputAttributes = Omit<InputHTMLAttributes<HTMLInputElement>, 'placeholder'>;

type InputProps = InputAttributes & {
  label: string;
  errorText?: string;
  status?: 'default' | 'error';
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
};

export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        value,
        id,
        label,
        disabled = false,
        status = 'default',
        errorText,
        required = false,
        iconBefore,
        iconAfter,
        className,
        autoComplete = 'off',
        ...rest
      },
      ref,
    ) => {
      const autoId = useId();
      const inputId = id ?? autoId;
      const errorTextId = `${inputId}-error`;
      const filled = Boolean(value);
      const hasError = status === 'error';
      const internalRef = useRef<HTMLInputElement | null>(null);

      const setRefs = (el: HTMLInputElement | null) => {
        internalRef.current = el;
        if (typeof ref === 'function') {
          ref(el);
        } else if (ref) {
          (ref as MutableRefObject<HTMLInputElement | null>).current = el;
        }
      };

      return (
        <MainWrapper className={cn(className, { required })}>
          <InputWrapper className={cn(status, { disabled })}>
            {Boolean(iconBefore) && iconBefore}
            <div className="field">
              <Label className={cn('label', { filled })} htmlFor={inputId}>
                {label}
              </Label>
              <StyledInput
                autoComplete={autoComplete}
                ref={setRefs}
                id={inputId}
                required={required}
                disabled={disabled}
                value={value}
                aria-invalid={hasError}
                aria-describedby={errorTextId}
                aria-required={required}
                className={cn({ filled })}
                {...rest}
              />
            </div>
            {Boolean(iconAfter) && iconAfter}
          </InputWrapper>
          {hasError && (
            <ErrorText id={errorTextId} role="alert">
              {errorText}
            </ErrorText>
          )}
        </MainWrapper>
      );
    },
  ),
);

Input.displayName = 'Input';

// ====== styles ======

const tokens = {
  height: '56px',
  radius: '8px',
  padX: '16px 16px',
  // colors
  text: COLORS.colorFieldText,
  danger: COLORS.colorFiledDanger,
  errorBg: COLORS.colorFieldErrorBg,
  default: COLORS.colorFieldBg,
  label: COLORS.colorFieldLabel,
  hover: COLORS.colorFieldHoverBg,
  borderColor: COLORS.colorFieldBorder,
};

const MainWrapper = styled.div`
  position: relative;
  height: fit-content;
  background: transparent;
  display: flex;
  flex-direction: column;
  row-gap: 2px;

  &.required::after {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${tokens.danger};
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const InputWrapper = styled.div`
  height: ${tokens.height};
  border-radius: ${tokens.radius};
  border: 1px solid ${tokens.default};
  display: flex;
  align-items: center;
  padding-inline: ${tokens.padX};
  gap: 12px;

  .field {
    width: 100%;
    height: 100%;
    position: relative;
  }

  &:hover input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px ${tokens.hover} inset;
  }

  input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px ${tokens.default} inset;
  }

  &:has(input:focus) {
    border-color: ${tokens.borderColor};
  }

  &.error {
    background: ${tokens.errorBg};
  }

  &:not(.disabled).error:hover {
    background: ${tokens.errorBg};
    filter: brightness(95%);
  }

  &.default {
    background: ${tokens.default};
  }

  &.disabled {
    opacity: 0.5;
  }

  &:not(.disabled):hover {
    background: ${tokens.hover};
  }
`;

const StyledInput = styled.input`
  appearance: none;
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  color: ${tokens.text};
  position: absolute;
  bottom: 50%;
  transform: translateY(50%);
  font-size: 14px;
  padding-block: 18px;
  transition: bottom var(--ani-speed) ease;

  &.filled {
    bottom: 8px;
    transform: none;
    font-size: 14px;
    padding-block: 0;
  }
`;

const Label = styled.label`
  position: absolute;
  width: 100%;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${tokens.label};
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  transition:
    top var(--ani-speed) ease,
    transform var(--ani-speed) ease;

  &.filled {
    top: 8px;
    transform: none;
  }
`;

const ErrorText = styled.div`
  position: absolute;
  top: ${tokens.height};
  font-size: 10px;
  padding-left: 16px;
  height: 12px;
  color: ${tokens.danger};
`;
